<template>
  <div>
    <div :class="[defaultClass, disabled ? disabledClass : '']">
      <button class="modal-section__container unstyled-btn w-full" @click="sectionClicked">
        <div class="modal-section__title">{{ title }}</div>
        <div class="modal-section__count" v-show="hasItems">({{ itemCount }})</div>
        <div class="modal-section__icon"></div>
      </button>
    </div>
    <div class="modal-section__preview" v-show="showPreviewItems">
      <PillItem
        v-for="(item, index) in itemsArray"
        :key="index"
        :show-as-link="true"
        :display-text-field="previewItemDisplayField"
        :data-model="item"
        @remove="removePreviewItem"
      />
    </div>
  </div>
</template>

<script>
import PillItem from '../form-fields/PillItem.vue';

export default {
  name: 'ModalSection',
  components: {
    PillItem,
  },
  emits: ['section-clicked', 'remove-preview-item'],
  props: {
    title: { default: 'Section Heading', type: String },
    items: { default: () => [], type: [Array, Object] },
    disabled: { default: false, type: Boolean },
    contextObject: { default: () => {}, type: Object },
    showPreview: { default: true, type: Boolean },
    previewItemDisplayField: { default: '', type: String },
    valueSymbol: { default: '', type: String },
  },
  data() {
    return {
      disabledClass: 'disabled',
      defaultClass: 'modal-section',
    };
  },
  computed: {
    itemsArray() {
      if (Array.isArray(this.items)) return this.items;

      // it's a range (min|max values)
      const listOfKeys = Object.keys(this.items);
      let prefix = '';
      let delimiter = ' - ';
      let rangeItem = listOfKeys
        .reduce((accumulator, keyName) => {
          let itemValue = this.items[keyName];
          const itemIsNumber = typeof itemValue === 'number';

          if (itemValue === null) {
            if (accumulator.length === 0) {
              // min value not specified
              prefix = 'Max ';
            } else {
              // max value not specified
              prefix = 'Min ';
            }
            delimiter = '';
          }

          if (itemIsNumber && this.valueSymbol === '$') {
            itemValue = Intl.NumberFormat().format(itemValue);
          }

          const finalConstructedValue = itemValue ? `${this.valueSymbol ?? ''}${itemValue}` : '';

          return [...accumulator, `${finalConstructedValue}`];
        }, [])
        .join(delimiter);

      rangeItem = `${prefix}${rangeItem}`;
      return [rangeItem];
    },
    itemCount() {
      if (this.items === undefined) return 0;
      if (Array.isArray(this.items)) return this.items.length;

      // it's a range
      if (typeof this.items === 'object' && Object.keys(this.items).length > 0) {
        return 1;
      }

      return 0;
    },
    hasItems() {
      return this.itemCount > 0;
    },
    showPreviewItems() {
      return this.showPreview && this.hasItems;
    },
    previewItems() {
      if (this.previewItemDisplayField !== '') {
        return this.items.map((preview) => preview[this.previewItemDisplayField]);
      }
      return this.items;
    },
  },
  methods: {
    sectionClicked(event) {
      event.preventDefault();
      event.stopPropagation();
      this.$emit('section-clicked', this.title, this.itemCount);
    },
    removePreviewItem() {
      this.$emit('remove-preview-item', ...arguments, this.contextObject); // send all the arguments
    },
  },
};
</script>
<style lang="scss">
.modal-section {
  background-color: $racq-pale-blue;
  border-radius: 5px;

  &.disabled {
    opacity: 0.5;

    &:hover {
      cursor: default;
    }
  }

  &:hover {
    cursor: pointer;
  }
}

.modal-section__count {
  color: $racq-dark-grey;
}

.modal-section__container {
  height: 50px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.modal-section__title {
  margin-left: 1rem;
  color: $racq-dark-grey;
}

.modal-section__icon {
  margin-left: auto;
  margin-right: 1.25rem;
  background-image: url(@/assets/img/chevron.svg);
  background-repeat: no-repeat;
  width: 18px;
  height: 10px;
  transform: rotate(90deg);
}

.modal-section__preview {
  display: flex;
  margin: 0.5rem 0;
  flex-wrap: wrap;

  @media (min-width: $breakpoint-md) {
    margin: 1rem 0;
  }
}
</style>
